import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import { useLanguage } from 'Context/LanguageContext';
import {
  Box, TextField, Typography, Tooltip, Stack, Chip, Button
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import {
  getInventoriesLinkedToAssetsRequest,
  deleteInventoriesLinkedToAssetsRequest,
  addLinkedInventoriesToTaskRequest,
} from '@core/api/asset/asset.service';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import DeleteIcon from '@mui/icons-material/Delete';
import PostAddIcon from '@mui/icons-material/PostAdd';
import LANGUAGE from 'FielderUtils/languages/fielderLanguages';
import SearchInventory from 'OldComponents/Inventory/SearchInventory';

const LinkAssetInventory = (
  {
    setInventoryValue,
    inventoryToAdd,
    idAsset,
    setInventoriesLinked,
    action,
    showInventorySearch,
    showDeleteIcon,
    idTask,
    reloadData,
    idAccountTask,
  }) => {
  const lan = useLanguage();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const fielderMsg = useFielderSnackBar();

  const langCode = lan?.getLanguage?.() ?? LANGUAGE.en;
  const localeMap = {
    [LANGUAGE.en]: MRT_Localization_EN,
    [LANGUAGE.es]: MRT_Localization_ES,
    [LANGUAGE.mx]: MRT_Localization_ES,
    [LANGUAGE.latam]: MRT_Localization_ES,
    [LANGUAGE.pt]: MRT_Localization_PT_BR,
  };
  const [inventoryList, setInventoryList] = useState([]);
  const [amount, setAmount] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [tableAssetInventory, setTableAssetInventory] = useState([]);
  const [showTableMoreInfo, setShowTableMoreInfo] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // memoize columns to avoid re-renders
  const columns = useMemo(() => ([
    { accessorKey: 'id', header: lan.id, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
    { accessorKey: 'name', header: lan.name, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
    { accessorKey: 'type', header: lan.type, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
    { accessorKey: 'model', header: lan.model, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
    { accessorKey: 'serialNumber', header: lan.serialNumber, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
    { accessorKey: 'brand', header: lan.brand, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
    { accessorKey: 'amount', header: lan.amount, Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography> },
  ]), [lan]);

  useEffect(() => {
    if (showTableMoreInfo) getInventoriesLinkedToAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, globalFilter]);

  useEffect(() => {
    if (showTableMoreInfo) getInventoriesLinkedToAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTableMoreInfo]);

  useEffect(() => {
    if (showTableMoreInfo && setInventoriesLinked) setInventoriesLinked(inventoryList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryList]);

  useEffect(() => {
    if (showTableMoreInfo) setShowTableMoreInfo(false);
  }, [idAsset]);

  const handleChange = (event) => {
    const raw = event.target.value;
    const digits = raw.replace(/\D+/g, '');
    const normalized = digits.replace(/^0+/, '');
    setAmount(normalized === '' ? 0 : parseInt(normalized, 10));
  };

  const addInventory = () => {
    if (!inventoryToAdd || amount <= 0) return;
    const id = inventoryToAdd.value?.id;
    // block duplicates – merge amounts instead
    setInventoryList((prev) => {
      const exists = prev.find((it) => it.value?.id === id);
      if (exists) {
        return prev.map((it) =>
          it.value?.id === id ? { ...it, amount: it.amount + amount } : it
        );
      }
      return [...prev, { label: inventoryToAdd.label, amount, value: inventoryToAdd.value }];
    });
  };

  const handleDelete = (id) => {
    setInventoryList((prev) => prev.filter((item) => item.value.id !== id));
  };

  const getInventoriesLinkedToAssets = async () => {
    const onSuccess = (response) => {
      if (response?.success) {
        let mergedList = [];
        const inventories = response.inventories ?? [];
        if (response.inventoryLinkedToAsset && inventories.length) {
          const amountMap = Object.fromEntries(
            response.inventoryLinkedToAsset.map((item) => [item.id, item.amount])
          );
          mergedList = inventories.map((item) => ({ ...item, amount: amountMap[item.id] ?? 0 }));
        }
        setTableAssetInventory(mergedList);
        setRowCount(response.countInventories ?? mergedList.length ?? 0);
      }
    };
    const onError = (error) => console.error('Error fetching inventories linked to asset:', error);

    const body = {
      idAsset,
      filter: globalFilter,
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      accountId: idAccountTask ?? user.idAccount,
    };
    const loadingConfig = { dispatch, config: { allowTopBar: true } };
    getInventoriesLinkedToAssetsRequest(body, onSuccess, onError, loadingConfig);
  };

  const showMoreInformation = (e) => {
    e.preventDefault();
    setShowTableMoreInfo((prev) => !prev);
  };

  const handleDeleteSelected = (selectedRows) => {
    const selectedIds = selectedRows.map((row) => ({
      id: row.original.id,
      amount: row.original.amount,
    }));
    const onSuccess = (response) => {
      if (response?.success) {
        setRowSelection({});
        getInventoriesLinkedToAssets();
      }
    };
    const onError = (error) => console.error('Error deleting linked inventories:', error);
    const loadingConfig = { dispatch, config: { allowTopBar: true } };
    deleteInventoriesLinkedToAssetsRequest({ inventoryLinkedToAsset: selectedIds, idAsset }, onSuccess, onError, loadingConfig);
  };

  const handleAddSelected = (selectedRows) => {
    const ids = selectedRows.map((row) => row.original.id);
    const body = { idInventories: ids, idTask };
    const onSuccess = () => {
      fielderMsg({
        title: lan.myOperation,
        message: lan.messageUpdatedProfile,
        variant: 'success',
        closeButton: true,
        duration: 3000,
      });
      reloadData();
    };
    const onError = (error) => console.error('Error adding linked inventories to task:', error);
    const loadingConfig = { dispatch, config: { allowTopBar: false } };
    addLinkedInventoriesToTaskRequest(body, onSuccess, onError, loadingConfig);
  };
  return (
    <Grid container>
      {showInventorySearch && (
        <Grid size={12}>
          <SearchInventory idAccount={user.idAccount} setInventoryValue={setInventoryValue} size={12} />
        </Grid>
      )}

      {inventoryToAdd && (
        <>
          <Grid size={12} container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={lan.amount}
                value={amount}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                onChange={handleChange}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}
            >
              <Button variant="contained" onClick={addInventory} sx={{ gap: 1, backgroundColor: '#dc5c87' }}>
                <PostAddIcon />
                <span>{lan.addInventory}</span>
              </Button>
            </Grid>
          </Grid>

          <Grid size={12} container justifyContent="center" sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {inventoryList.map((inventory) => {
                const label = inventory.label + (inventory.amount >= 1 ? ` (x${inventory.amount})` : '');
                const id = inventory.value?.id ?? label;
                return (
                  <Tooltip key={id} title={label}>
                    <Chip
                      label={label}
                      size="small"
                      onDelete={() => handleDelete(inventory.value.id)}
                      sx={{
                        backgroundColor: '#dc5c87',
                        color: '#ffffffff',
                        '& .MuiChip-deleteIcon': { color: '#ffffffff' },
                        '& .MuiChip-deleteIcon:hover': { color: '#ffffffff' },
                        maxWidth: { xs: '100%', sm: 360, md: 480, lg: 550, xl: 650 },
                        minWidth: 0,
                        flexShrink: 1,
                        overflow: 'hidden',
                        '& .MuiChip-label': {
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          </Grid>
        </>
      )}

      {action === 'update' && (
        <>
          <Grid
            container
            size={12} sx={{ mb: 2, mt: 2, justifyContent: "flex-start", alignItems: "center" }} direction="row">
            <Grid size={6}>
              <Button
                id='show-more-info-button'
                onClick={showMoreInformation}
                variant="outlined"
                size={'small'}
                fullWidth
              >
                {lan.viewAssociatedInventories}
              </Button>
            </Grid>
            {/*<Link
              href="#"
              onClick={showMoreInformation}
              sx={{
                color: '#1565c0',
                textDecoration: 'underline',
                textDecorationColor: '#1565c0',
                '&:hover': { color: '#dc5c87', textDecorationColor: '#dc5c87' },
              }}
            >
              {lan.viewAssociatedInventories}
            </Link>*/}
          </Grid>
          {showTableMoreInfo && (
            <MaterialReactTable
              columns={columns}
              data={tableAssetInventory}
              rowCount={rowCount}
              manualPagination
              enableGlobalFilter
              enableColumnFilters
              state={{ pagination, globalFilter, rowSelection, columnFilters }}
              onRowSelectionChange={setRowSelection}
              onColumnFiltersChange={setColumnFilters}
              onPaginationChange={setPagination}
              onGlobalFilterChange={setGlobalFilter}
              localization={localeMap[langCode]}
              enableRowSelection
              renderTopToolbarCustomActions={({ table }) => {
                const selectedRows = table.getSelectedRowModel().flatRows;
                const disabled = selectedRows.length === 0;
                return (
                  <Box sx={{ ml: 1, mt: 1, display: 'flex', gap: 1 }}>
                    {showDeleteIcon ? (
                      <Tooltip title={lan.delete}>
                <span>
                  <Button
                    startIcon={<DeleteIcon />}
                    size="small"
                    disabled={disabled}
                    onClick={() => handleDeleteSelected(selectedRows)}
                  >
                    {lan.delete}
                  </Button>
                </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={lan.addInventory}>
                <span>
                  <Button
                    startIcon={<PostAddIcon />}
                    size="small"
                    disabled={disabled}
                    onClick={() => handleAddSelected(selectedRows)}
                  >
                    {lan.addInventory}
                  </Button>
                </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              }}
            />
          )}
        </>
      )}
    </Grid>
  );
};
export default LinkAssetInventory;
//Original code:
/*import React, { useState, useEffect, useReducer, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import { useLanguage } from 'Context/LanguageContext';
import { Link, Box, TextField, Typography, Tooltip, Stack, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import {
  getInventoriesLinkedToAssetsRequest,
  deleteInventoriesLinkedToAssetsRequest,
  addLinkedInventoriesToTaskRequest,
} from '@core/api/asset/asset.service';
import { useDispatch } from 'react-redux';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import AddIcon from '@assets/icons/plus.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import PostAddIcon from '@mui/icons-material/PostAdd';
import LANGUAGE from 'FielderUtils/languages/fielderLanguages';
import SearchInventory from 'OldComponents/Inventory/SearchInventory';
import { ThemeProvider } from '@mui/material/styles';
import { createSmartFielderTheme } from '~/FielderElements/SmartTaskTable/SmartStyles';

const LinkAssetInventory = ({setInventoryValue, inventoryToAdd, idAsset, setInventoriesLinked,
    action, showInventorySearch, showDeleteIcon, idTask, reloadData, idAccountTask}) => {

 	const lan = useLanguage();
	const dispatch = useDispatch();
	const user = useSelector(selectUser);
	const fielderMsg = useFielderSnackBar();
	const langCode = lan?.getLanguage?.() ?? LANGUAGE.en;
	const localeMap = {
		[LANGUAGE.en]: MRT_Localization_EN,
		[LANGUAGE.es]: MRT_Localization_ES,
		[LANGUAGE.mx]: MRT_Localization_ES,
		[LANGUAGE.latam]: MRT_Localization_ES,
		[LANGUAGE.pt]: MRT_Localization_PT_BR,
	};
	const [mode, setMode] = useState('light');
	const smartTheme = useMemo(() => createSmartFielderTheme(mode), [mode]);
	const [inventoryList, setInventoryList] = useState([]);
	const [amount, setAmount] = useState(1);
	const [rowCount, setRowCount] = useState(0);
	const [globalFilter, setGlobalFilter] = useState('');
	const [columnFilters, setColumnFilters] = useState([]);
	const [tableAssetInventory, setTableAssetInventory] = useState([]);
	const [showTableMoreInfo, setShowTableMoreInfo] = useState(false);
	const [rowSelection, setRowSelection] = useState({});
	const [pagination, setPagination] = useState({
		pageIndex: 0,
		pageSize: 10,
	});

	useEffect(() => {
		if (showTableMoreInfo) getInventoriesLinkedToAssets();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.pageIndex, pagination.pageSize, globalFilter]);

	useEffect(() => {
		if (showTableMoreInfo) getInventoriesLinkedToAssets();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showTableMoreInfo]);

	useEffect(() => {
		if (showTableMoreInfo && setInventoriesLinked !== undefined) setInventoriesLinked(inventoryList);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inventoryList]);

	useEffect(() => {
		if (showTableMoreInfo) setShowTableMoreInfo(false);
	}, [idAsset]);

	const handleChange = (event) => {
		let value = event.target.value;
		value = value.replace(/[^0-9]/g, '');
		if (value.startsWith('0')) value = value.replace(/^0+/, '');
		if (value === '') {
			setAmount(value);
		} else {
			setAmount(parseInt(value));
		}
	};

	const addInventory = () => {
		if (inventoryToAdd && amount > 0) {
			const inventory = {
				label: inventoryToAdd.label,
				amount: amount,
				value: inventoryToAdd.value,
			};
			setInventoryList((prevList) => [...prevList, inventory]);
		}
  	};

	const handleDelete = (id) => {
		setInventoryList((prevList) => prevList.filter((item) => item.value.id !== id));
	};

	const getInventoriesLinkedToAssets = async () => {
		const onSuccess = (response) => {
		if (response.success) {
			let mergedList = [];
			if (response.inventoryLinkedToAsset && response.inventories.length) {
			const amountMap = Object.fromEntries(response.inventoryLinkedToAsset.map((item) => [item.id, item.amount]));
			mergedList = response.inventories.map((item) => ({
				...item,
				amount: amountMap[item.id] ?? 0,
			}));
			}
			setTableAssetInventory(mergedList ? mergedList : []);
			setRowCount(response.countInventories);
		}
		};
		const onError = (error) => {
			console.error('Error fetching inventories linked to asset:', error);
		};
		const body = { idAsset, filter: globalFilter, offset: pagination.pageIndex * pagination.pageSize, limit: pagination.pageSize, accountId: idAccountTask ?? user.idAccount };
		const loadingConfig = { dispatch, config: { allowTopBar: true } };
		getInventoriesLinkedToAssetsRequest(body, onSuccess, onError, loadingConfig);
	};

	const showMoreInformation = (e) => {
		e.preventDefault();
		setShowTableMoreInfo(!showTableMoreInfo);
	};

	const handleDeleteSelected = (selectedRows) => {
		const selectedIds = selectedRows.map((row) => ({
		id: row.original.id,
		amount: row.original.amount,
		}));
		const onSuccess = (response) => {
		if (response.success) {
			setRowSelection({});
			getInventoriesLinkedToAssets();
		}
		};
		const onError = (error) => {
		console.error('Error deleting linked inventories:', error);
		};
		const loadingConfig = { dispatch, config: { allowTopBar: true } };
		deleteInventoriesLinkedToAssetsRequest({ inventoryLinkedToAsset: selectedIds, idAsset }, onSuccess, onError, loadingConfig);
	};

	const handleAddSelected = (selectedRows) => {
		const ids = selectedRows.map((row) => row.original.id);
		const body = { idInventories: ids, idTask };
		const onSuccess = (response) => {
		console.log('response', response);
		fielderMsg({
			title: lan.myOperation,
			message: lan.messageUpdatedProfile,
			variant: 'success',
			closeButton: true,
			duration: 3000,
		});
		reloadData();
		};
		const onError = (error) => {
		console.error('Error adding linked inventories to task:', error);
		};
		const loadingConfig = { dispatch, config: { allowTopBar: false } };
		addLinkedInventoriesToTaskRequest(body, onSuccess, onError, loadingConfig);
	};

	const columns = [
		{
		accessorKey: 'id',
		header: lan.id,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
		{
		accessorKey: 'name',
		header: lan.name,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
		{
		accessorKey: 'type',
		header: lan.type,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
		{
		accessorKey: 'model',
		header: lan.model,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
		{
		accessorKey: 'serialNumber',
		header: lan.serialNumber,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
		{
		accessorKey: 'brand',
		header: lan.brand,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
		{
		accessorKey: 'amount',
		header: lan.amount,
		Cell: ({ cell }) => <Typography variant="body2">{cell.getValue()}</Typography>,
		},
	];

	return (
		<>
		<Grid container>
			{showInventorySearch && (
			<Grid size={12}>
				<SearchInventory idAccount={user.idAccount} setInventoryValue={setInventoryValue} size={12} />
			</Grid>
			)}
			{inventoryToAdd && (
			<>
				<Grid size={12} container spacing={2} justifyContent="center" sx={{ mt: 3 }}>
				<Grid size={{ xs: 12, md: 3 }}>
					<TextField fullWidth label={lan.amount} defaultValue={amount} onChange={handleChange} />
				</Grid>
				<Grid
					size={{ xs: 12, md: 4 }}
					sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}
				>
					<button className="AddForm-button" onClick={addInventory}>
					<img src={AddIcon} alt="" />
					<span>{lan.addInventory}</span>
					</button>
				</Grid>
				</Grid>
				<Grid size={12} container justifyContent="center" sx={{ mt: 2 }}>
					<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
						{inventoryList.map((inventory, index) => {
						const label =
							inventory.label + (inventory.amount >= 1 ? ` (x${inventory.amount})` : '');
						return (
							<Tooltip key={index} title={label}>
							<Chip
								label={label}
								size="small"
								onDelete={() => handleDelete(inventory.value.id)}
								sx={{
								backgroundColor: '#dc5c87',
								color: '#ffffffff',
								'& .MuiChip-deleteIcon': { color: '#ffffffff' },
								'& .MuiChip-deleteIcon:hover': { color: '#ffffffff' },

								// responsive sizing + truncation
								maxWidth: { xs: '100%', sm: 360, md: 480, lg: 550, xl: 650 },
								minWidth: 0,
								flexShrink: 1,
								overflow: 'hidden',
								'& .MuiChip-label': {
									display: 'block',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								},
								}}
							/>
							</Tooltip>
						);
						})}
					</Stack>
				</Grid>
			</>
			)}
			{action === 'update' && (
			<>
				<Grid size={12} sx={{ mb: 2, mt: 2 }}>
				<Link
					href="#"
					onClick={showMoreInformation}
					sx={{
					color: '#1565c0',
					textDecoration: 'underline',
					textDecorationColor: '#1565c0',
					'&:hover': { color: '#dc5c87', textDecorationColor: '#dc5c87' },
					}}
				>
					{lan.viewAssociatedInventories}
				</Link>
				</Grid>
				{showTableMoreInfo && (
				<ThemeProvider theme={smartTheme}>
					<MaterialReactTable
					columns={columns}
					data={tableAssetInventory}
					rowCount={rowCount}
					manualPagination
					enableGlobalFilter
					enableColumnFilters
					state={{ pagination, globalFilter, rowSelection, columnFilters }}
					onRowSelectionChange={setRowSelection}
					onColumnFiltersChange={setColumnFilters}
					onPaginationChange={setPagination}
					onGlobalFilterChange={setGlobalFilter}
					localization={localeMap[langCode]}
					enableRowSelection
					renderTopToolbarCustomActions={({ table }) => {
						const selectedRows = table.getSelectedRowModel().flatRows;
						return (
						<Box sx={{ ml: 1, mt: 1 }}>
							{showDeleteIcon ? (
							<Tooltip title={lan.delete}>
								<DeleteIcon
								sx={{
									mr: 1,
									color: selectedRows.length === 0 ? 'grey' : '#dc5c87',
									cursor: selectedRows.length === 0 ? 'not-allowed' : 'pointer',
								}}
								onClick={() => handleDeleteSelected(selectedRows)}
								/>
							</Tooltip>
							) : (
							<Tooltip title={lan.addInventory}>
								<PostAddIcon
								sx={{
									mr: 1,
									color: selectedRows.length === 0 ? 'grey' : '#dc5c87',
									cursor: selectedRows.length === 0 ? 'not-allowed' : 'pointer',
								}}
								onClick={() => handleAddSelected(selectedRows)}
								/>
							</Tooltip>
							)}
						</Box>
						);
					}}
					/>
				</ThemeProvider>
				)}
			</>
			)}
		</Grid>
		</>
	);
};

export default LinkAssetInventory;
*/
